import { type SnapshotFrom, waitFor } from 'xstate';

import type { BasketRowResponse, Cart, SumType } from 'models/api';
import type { ProductPrice } from 'models/price';
import type { MarketCode } from 'models/sitecore';
import type { CheckoutMachineActor } from 'state-machines/checkout';
import { asArray, sleep } from 'utils/helpers';

import type {
	GTMCurrency,
	GTMItemListId,
	GTMItemListName,
	ListItemGTMProduct,
	Purchase,
} from './GTMEvents';

export type GTMListProduct =
	| { id: string; index?: number }
	| { index?: number; variantId: string };

export type GTMPriceProduct =
	| { displayPrice?: ProductPrice; id: string }
	| { id: string; price?: ProductPrice }
	| { price?: ProductPrice; variantId: string };

export interface GTMPriceProductWithQuantity {
	itemListId: GTMItemListId | undefined;
	itemListName: GTMItemListName | undefined;
	product: GTMPriceProduct;
	quantity: number;
}

export const MARKET_CURRENCIES: Record<MarketCode, GTMCurrency> = {
	AT: 'EUR',
	DE: 'EUR',
	FI: 'EUR',
	NO: 'NOK',
	PL: 'PLN',
	SE: 'SEK',
};

function getPrice(product: GTMPriceProduct): number {
	if ('price' in product) {
		return product.price?.priceIncVat?.value ?? 0;
	}
	if ('displayPrice' in product) {
		return product.displayPrice?.priceIncVat?.value ?? 0;
	}
	return 0;
}

export function getItemsWithPrice(
	items: GTMPriceProductWithQuantity | GTMPriceProductWithQuantity[],
) {
	return asArray(items).map((item) => {
		const listParams =
			item.itemListId && item.itemListName
				? { item_list_id: item.itemListId, item_list_name: item.itemListName }
				: {};

		return {
			item_id: 'id' in item.product ? item.product.id : item.product.variantId,
			quantity: item.quantity,
			price: getPrice(item.product),
			...listParams,
		};
	});
}

export function getItemsWithPriceAndCurrency(
	items: GTMPriceProductWithQuantity | GTMPriceProductWithQuantity[],
	currency: GTMCurrency,
) {
	return getItemsWithPrice(items).map((item) => ({ ...item, currency }));
}

export function getCartItemsWithPrice(rows: BasketRowResponse[]) {
	return getItemsWithPrice(
		rows.map((variant) => ({
			product: variant,
			quantity: variant.quantity,
			itemListId: variant.metaData?.gtmItemListId,
			itemListName: variant.metaData?.gtmItemListName,
		})),
	);
}

export function getListItemProductData({
	index,
	itemListId,
	itemListName,
	product,
}: {
	index: number;
	itemListId: GTMItemListId;
	itemListName: GTMItemListName;
	product: GTMListProduct;
}) {
	return {
		index,
		item_id: 'id' in product ? product.id : product.variantId,
		item_list_id: itemListId,
		item_list_name: itemListName,
	};
}

export function getWishlistTotalValue(
	product: GTMPriceProduct,
	quantity: number,
): number {
	return getPrice(product) * quantity;
}

export function getProductDataList({
	itemListId,
	itemListName,
	pageSize,
	products,
}: {
	itemListId: GTMItemListId;
	itemListName: GTMItemListName;
	pageSize: number;
	products: GTMListProduct[];
}) {
	// TODO: Think about pageSize vs. product.index (the latter was added for
	// carousels), setting index makes pageSize irrelevant. Separate events?
	let pages = Math.trunc(products.length / pageSize);
	pages += products.length % pageSize > 0 ? 1 : 0;
	const startIndex = pages > 1 ? (pages - 1) * pageSize : 0;

	const items: ListItemGTMProduct[] = [];
	for (let i = startIndex; i < products.length; i++) {
		const product = products[i]!;
		items.push(
			getListItemProductData({
				product,
				index: product.index ?? i + 1,
				itemListId,
				itemListName,
			}),
		);
	}

	return items;
}

function getSumValue(cart: Cart, sumType: SumType): number {
	return (
		cart.totalSummary.incVat.find((sum) => sum.sumType === sumType)?.value ?? 0
	);
}

export function getPurchaseData({
	cart,
	marketCode,
}: {
	cart: Cart;
	marketCode: MarketCode;
}): Purchase['ecommerce'] {
	const coupons = [
		cart.campaignDiscount?.checkNumber,
		cart.freightDiscount?.checkNumber,
	].filter(Boolean);

	return {
		cart_id: cart.id,
		consent: {
			review_opt_in: cart.allowAnonymousReviews,
			email: cart.allowAnonymousReviews ? (cart.email ?? '') : '',
		},
		coupon: coupons.length > 0 ? coupons.join(',') : undefined,
		currency: MARKET_CURRENCIES[marketCode],
		items: getCartItemsWithPrice(cart.basketRows),
		payment_type: cart.selectedPayment?.selectedPaymentType ?? '',
		shipping: getSumValue(cart, 'Freight'),
		shipping_tier: cart.selectedDelivery?.deliveryMethodId ?? '',
		tax: getSumValue(cart, 'Vat'),
		transaction_id: cart.orderNumber!,
		value: getSumValue(cart, 'Total'),
	};
}

export function getCartValueAndItems({
	cart,
	marketCode,
}: {
	cart: Cart;
	marketCode: MarketCode;
}) {
	return {
		value: getSumValue(cart, 'Total'),
		items: getCartItemsWithPrice(cart.basketRows),
		currency: MARKET_CURRENCIES[marketCode],
	};
}
export async function waitForServiceIdle<
	TActorRef extends CheckoutMachineActor,
>(
	service: TActorRef,
	matcher: (emitted: SnapshotFrom<TActorRef>) => boolean,
): Promise<SnapshotFrom<TActorRef>> {
	// Wait a bit in case this helper gets called before the machine moves
	// away from idle, e.g.
	// sendAddToCartEvent(...);
	// cartService.send(...)
	await sleep(50);
	return waitFor(service, matcher);
}
